/**
 * The surface pattern presets and their sources (STYLE §15). Every value is cited; values no source
 * gives are marked as assumptions. Agents send new presets and changed values through the
 * coordinator, with their sources.
 */

import type { PATTERN } from './patternIds';
import type {
  BoardsPreset,
  BrickPreset,
  HollePanPreset,
  LeadPreset,
  LeadedGlassPreset,
  ParamSource,
  PatternPreset,
  PavingPreset,
  PilingPreset,
  PlankingPreset,
  SailclothPreset,
  SlatePreset,
} from './presetTypes';

type PresetName = Exclude<keyof typeof PATTERN, 'none'>;

const VERMEER_203 =
  'Vermeer, "Metselwerk in de oude binnenstad van Amsterdam" 1, Binnenstad 203 (2003): mortar of shell lime, cream against the red Vecht brick';
const VERMEER_204 =
  'Vermeer, Binnenstad 204 (2004): klezoren stacked plumb at face ends and beside openings; a brick is two headers and a joint long';
const HOUSES_BRICK =
  'houses agent, round 17: 25 x 12 x 6 cm (Zeedijk 1 older wall 25 x 12.5 x 6.5, Huizen in Nederland II; Waag 1480 and Bank van Lening 1550 25 cm long, 5.5 cm thick); staand verband, cross bond being dated from 1500 (Vermeer 204) or 1550 (Rouwhorst, Binnenstad 254)';
const JANSE_2004 =
  'Janse, De Oude Kerk te Amsterdam (2004, DBNL), via landmarks: 14th-century tower body of bricks 27-29 cm long and 6-7 cm thick in early staand verband with very thick joints; a red-sandstone quoin course after every 6 courses, quoin stones 30-60 cm long';
const DE_GRAAUW_2011 =
  'De Graauw, Bulletin KNOB 2011, via landmarks: 15th-16th-century red brick 22 x 10-10.5 x 5.5-6 cm, ten courses 69.5-70 cm, in kruisverband';
const SPEKLAGEN =
  'Janse 2004 and De Graauw 2011, via landmarks: sandstone speklagen 2 courses high after every 7 courses on the clerestory of 1510; dressings in the palette range wallLight to highlightWhite';
const GOBERTANGE =
  'De Graauw, Bulletin KNOB 2011, p. 121, via landmarks-2: on the Sint-Anthonispoort (the Waag) Gobertange speklagen, one course high, every 20-27 courses of brick, on every part of the main gate but the Geldersekade front and up to about 5.5 m';
const LEDESTEEN =
  'AAR 95, pp. 30-31, via landmarks-2: bands of Ledesteen 11.5-12 cm high every 10-11 courses of brick';
const AAR95_SANDSTONE =
  'AAR 95 (Gemeente Amsterdam 2017, De Waag op de Nieuwmarkt), via landmarks-2: the sandstone ("zandsteen", Bentheim never named) plinth shells of the Sint-Anthonispoort, courses 46.0 cm high (K and TA, p. 30) and 51.0-52.0 cm (the Schutterstoren, p. 35); the east front plinth, one course of blocks 85.0-120.0 cm long (p. 36), the only block length the sources give';
const HOUSES_BOARDS =
  'houses agent, round 17: vertical planks butt-jointed without battens, 0.22 m mean varying 15% (Begijnhof 34, whose 1956-57 boarding copied the original planks; medium-low); joints about 1 cm at 0.85 of the base luminance; board tone 4%';
const HOLLE_PAN =
  'Ons Amsterdam, "Onder de pannen": the Oude Holle, a pan with its overlap (wel) mostly on the right, from the second half of the 15th century; houses agent: hung on battens, gauge 0.30 m and cover 0.20 m (low-medium, from reproductions: 343 x 242 mm, 202 mm cover, 304-306 mm gauge)';
const HIGHLIGHT_STROKES =
  "palette.json highlightWhite: the painting's light highlight strokes, among them the tile courses";
const MAAS =
  'houses and landmarks agents, round 17: Maasdekking of 28 x 23 cm slates in 11 cm courses on oak boarding (RCE, Leien in Maasdekking); no Amsterdam source says whether the 1538 church roofs were coursed Maas or Rhenish';
const LEAD =
  'Lead Sheet Association bay sizes for code 5-6 sheet (rolls 600-675 mm apart, drips 2.0-2.25 m); sand-cast lead covered church roofs until the 18th century (buildingconservation.com, Church Leadwork); no Amsterdam source';
const QUARRIES =
  'Metropolitan Museum 198899, an English quarry of c. 1485-1509, 15.4 cm high; diamond quarries are typical of the 16th century (buildingconservation.com, Historic Leaded Lights); landmarks: quarries in stone tracery, choir windows 4 lights by 7 panels';
const HULL =
  'ships agent, round 17: carvel strakes with butts staggered, planks 6-8 m (period norm, low); clinker still usual in 1538 (Verweij, Waldus and Van Holk 2012); seams and wale shadows #201600, L* 8.2, 26% of hull pixels in the painting; the hull darkens toward the waterline from about L* 30 to 20';
const SAIL =
  'ships agent, round 17: vertical seams 0.65 m apart (0.56-0.76; the woodcut draws about 15 cloths across a main course), 3-4 cm wide and slightly darker than the cloth; a bonnet lacing at about 0.78 of the course depth (medium)';

const JUK =
  "streetscape agent, plan §3.2 (Jayasena, pp.146-147 and p.156 note 624): the juk's squared oak posts, 18 x 18 cm at the Dijkstraat revetment (1500-25), 0.50 m apart centre to centre (IJ front, 1387-1424; the other dated revetments bracket it at 0.30-0.60 m); the instanced posts (quayTimbers.ts JUK) stand at u = (k + 0.5) x 0.50 m within 60 m";
const PILING_TONES =
  'streetscape agent, plan §6.5: posts 2.62 times as light as the planking between them in linear light, on the painted revetment west of the Nieuwe Brug (museum scan bands A 4718-4765 x 3288-3310, 2.70, and B 4775-4832 x 3272-3294, 2.50; the lightest 36% of pixels against the rest); the face keeps shipHull #42330f as its mean, so posts are 1.655 and planking 0.632 of it';

const ALKMAAR =
  "streetscape agent, plan §3.1.1 and §5.3: the Master of Alkmaar's Seven Works of Mercy (1504, Rijksmuseum), read in the museum's viewer; L for Amsterdam 1538";
const PAVING_PITCH = `${ALKMAAR}: the pitch is 3.5-14.3 cm over every reference and 5-9 cm where most of them agree; each owner kept the street before his house and in 1524 the paving still changed from one front to the next (Amsterdamse Vereniging, citing Stadsarchief 5020), so each front takes its own pitch`;
const PAVING_OUTLINE = `${ALKMAAR}: the painted outline is 1-2 image px wide at every depth; in linear light it is 0.69-0.75 of the paving's mean, median 0.74, where the stones stand 51-130 px apart (panels 1, 2, 3 and 5), and 0.84 where they stand 18-25 px apart (panel 1's middle ground)`;
const PAVING_SPREAD = `${ALKMAAR}: (p90 - p10) / (p90 + p10) of the lightest 90 % of paving pixels is 0.11-0.15, with each stone's shading and the craquelure in it, so a stone-to-stone spread is at most that`;

function assumed(params: readonly string[], why: string): ParamSource {
  return { params, source: `Assumption: ${why}`, confidence: 'assumption' };
}

const houseBrick1538: BrickPreset = {
  kind: 'brick',
  surfaceClass: 'wallBrick',
  brickLengthM: [0.25, 0.25],
  brickHeightM: [0.06, 0.06],
  jointM: 0.011,
  bond: 'staand',
  closers: true,
  mortar: { paletteClass: 'highlightWhite' },
  toneSpread: 0.06,
  sources: [
    {
      params: ['brickLengthM', 'brickHeightM', 'bond'],
      source: HOUSES_BRICK,
      confidence: 'medium',
    },
    { params: ['closers'], source: VERMEER_204, confidence: 'medium' },
    {
      params: ['mortar'],
      source: `${VERMEER_203}; the palette's lightest cream`,
      confidence: 'medium',
    },
    assumed(['jointM'], 'houses give 1.0-1.2 cm without a source; the mean is used'),
    assumed(['toneSpread'], 'per-brick luminance 6% either way, inside wallBrick p10-p90'),
  ],
};

const timberFront1538: BoardsPreset = {
  kind: 'boards',
  surfaceClass: 'wallLight',
  boardWidthM: 0.22,
  widthVariation: 0.15,
  jointM: 0.01,
  joint: { baseTimes: 0.85 },
  toneSpread: 0.04,
  sources: [
    {
      params: ['boardWidthM', 'widthVariation', 'jointM', 'joint', 'toneSpread'],
      source: HOUSES_BOARDS,
      confidence: 'medium-low',
    },
  ],
};

const roofTileHollePan: HollePanPreset = {
  kind: 'hollePan',
  surfaceClass: 'roofTile',
  gaugeM: 0.3,
  coverWidthM: 0.2,
  lapping: 'right',
  course: { paletteClass: 'highlightWhite' },
  courseWidthM: 0.02,
  rollContrast: 0.08,
  toneSpread: 0.06,
  sources: [
    { params: ['gaugeM', 'coverWidthM', 'lapping'], source: HOLLE_PAN, confidence: 'low' },
    { params: ['course'], source: HIGHLIGHT_STROKES, confidence: 'medium' },
    assumed(['courseWidthM'], 'the course stroke 2 cm, a tenth of the cover'),
    assumed(['rollContrast'], "8% light on a pan's roll and dark in its trough"),
    assumed(['toneSpread'], 'per-pan luminance 6% either way, inside roofTile p10-p90'),
  ],
};

const slateMaas: SlatePreset = {
  kind: 'slate',
  surfaceClass: 'roofSlate',
  slateWidthM: 0.23,
  courseM: 0.11,
  jointM: 0.004,
  courseShadow: { baseTimes: 0.8 },
  courseShadowM: 0.012,
  toneSpread: 0.05,
  sources: [
    { params: ['slateWidthM', 'courseM'], source: MAAS, confidence: 'low' },
    assumed(['jointM'], '4 mm between slates'),
    assumed(['courseShadow', 'courseShadowM'], "a 12 mm shadow at 0.8 under each course's edge"),
    assumed(['toneSpread'], 'per-slate luminance 5% either way, inside roofSlate p10-p90'),
  ],
};

const brickOudeKerkTower: BrickPreset = {
  kind: 'brick',
  surfaceClass: 'towerStone',
  brickLengthM: [0.27, 0.29],
  brickHeightM: [0.06, 0.07],
  jointM: 0.02,
  bond: 'staand',
  closers: true,
  mortar: { paletteClass: 'highlightWhite' },
  toneSpread: 0.06,
  band: {
    everyCourses: 6,
    courses: 1,
    colour: { paletteClass: 'roofTile' },
    cornerLengthM: [0.3, 0.6],
  },
  sources: [
    {
      params: ['brickLengthM', 'brickHeightM', 'bond', 'band'],
      source: JANSE_2004,
      confidence: 'medium',
    },
    assumed(
      ['band'],
      "red sandstone drawn in roofTile, the palette's red; the painting does not resolve the quoins"
    ),
    { params: ['closers'], source: VERMEER_204, confidence: 'low' },
    { params: ['mortar'], source: VERMEER_203, confidence: 'medium' },
    assumed(['jointM'], 'Janse gives "very thick" joints without a size; 2 cm'),
    assumed(['toneSpread'], 'per-brick luminance 6% either way, inside towerStone p10-p90'),
  ],
};

const lateGothicBrick = {
  kind: 'brick',
  surfaceClass: 'wallBrick',
  brickLengthM: [0.22, 0.22],
  brickHeightM: [0.055, 0.06],
  jointM: 0.012,
  bond: 'kruis',
  closers: true,
  mortar: { paletteClass: 'highlightWhite' },
  toneSpread: 0.06,
} as const;

const lateGothicSources: readonly ParamSource[] = [
  {
    params: ['brickLengthM', 'brickHeightM', 'bond', 'jointM'],
    source: `${DE_GRAAUW_2011}; the joint from the ten-course measure`,
    confidence: 'medium',
  },
  { params: ['closers'], source: VERMEER_204, confidence: 'medium' },
  { params: ['mortar'], source: VERMEER_203, confidence: 'medium' },
  assumed(['toneSpread'], 'per-brick luminance 6% either way, inside wallBrick p10-p90'),
];

const brickLateGothic: BrickPreset = { ...lateGothicBrick, sources: lateGothicSources };

const brickLateGothicSpeklagen: BrickPreset = {
  ...lateGothicBrick,
  band: { everyCourses: 7, courses: 2, colour: { paletteClass: 'wallLight' } },
  sources: [...lateGothicSources, { params: ['band'], source: SPEKLAGEN, confidence: 'medium' }],
};

const brickLateGothicGobertange: BrickPreset = {
  ...lateGothicBrick,
  band: { everyCourses: [20, 27], courses: 1, colour: { paletteClass: 'wallLight' } },
  sources: [
    ...lateGothicSources,
    { params: ['band'], source: GOBERTANGE, confidence: 'medium' },
    assumed(['band'], `the stone drawn in wallLight, as the speklagen are (${SPEKLAGEN})`),
  ],
};

const brickLateGothicLedesteen: BrickPreset = {
  ...lateGothicBrick,
  band: { everyCourses: [10, 11], courses: 2, colour: { paletteClass: 'wallLight' } },
  sources: [
    ...lateGothicSources,
    { params: ['band'], source: LEDESTEEN, confidence: 'medium' },
    assumed(
      ['band'],
      'the 11.5-12 cm band drawn as the two courses of Brick B nearest it (13.9 cm), a band being whole courses; the stone in wallLight, as the speklagen are'
    ),
  ],
};

const sandstoneAshlar: BrickPreset = {
  kind: 'brick',
  surfaceClass: 'towerStone',
  brickLengthM: [0.85, 1.2],
  brickHeightM: [0.46, 0.52],
  jointM: 0.01,
  bond: 'ashlar',
  closers: false,
  mortar: { paletteClass: 'highlightWhite' },
  toneSpread: 0.05,
  sources: [
    { params: ['brickLengthM', 'brickHeightM'], source: AAR95_SANDSTONE, confidence: 'medium' },
    assumed(['bond'], 'ashlar blocks in courses, each course half a block off the one below'),
    assumed(['closers'], 'ashlar needs no closers'),
    assumed(
      ['jointM'],
      'no source gives the joint (AAR 95 names only a later cement joint, p. 36); 1 cm'
    ),
    { params: ['mortar'], source: VERMEER_203, confidence: 'low' },
    assumed(['toneSpread'], 'per-block luminance 5% either way, inside towerStone p10-p90'),
  ],
};

const leadSheet: LeadPreset = {
  kind: 'lead',
  surfaceClass: 'roofSlate',
  bayWidthM: 0.6,
  sheetLengthM: 2.25,
  rollWidthM: 0.05,
  roll: { baseTimes: 1.15 },
  toneSpread: 0.05,
  sources: [
    { params: ['bayWidthM', 'sheetLengthM'], source: LEAD, confidence: 'low' },
    assumed(['rollWidthM', 'roll'], 'a 5 cm roll catching light at 1.15'),
    assumed(['toneSpread'], 'sand-cast sheet mottled 5% either way'),
  ],
};

const leadedGlass: LeadedGlassPreset = {
  kind: 'leadedGlass',
  surfaceClass: 'lineworkDark',
  quarryWidthM: 0.1,
  quarryHeightM: 0.154,
  cameWidthM: 0.008,
  came: { baseTimes: 0.6 },
  saddleBarM: 0.015,
  toneSpread: 0.1,
  sources: [
    { params: ['quarryHeightM'], source: QUARRIES, confidence: 'low' },
    assumed(['quarryWidthM'], 'the diamond two thirds as wide as high'),
    assumed(
      ['cameWidthM', 'came', 'saddleBarM'],
      'cames 8 mm at 0.6 of the glass, which is already the dark half of lineworkDark; saddle bars 15 mm'
    ),
    assumed(['toneSpread'], 'old glass varies 10% either way from quarry to quarry'),
  ],
};

const hull = {
  surfaceClass: 'shipHull',
  plankLengthM: [6, 8],
  seam: { measured: '#201600' },
  buttWidthM: 0.02,
  waterlineShade: 0.48,
  waterlineDepthM: 1,
  toneSpread: 0.04,
} as const;

function hullSources(seam: string): readonly ParamSource[] {
  return [
    {
      params: ['lap', 'plankLengthM', 'seam', 'waterlineShade'],
      source: `${HULL}; the waterline shade is the luminance of L* 20 over L* 30`,
      confidence: 'low',
    },
    assumed(['seamFraction'], seam),
    assumed(['buttWidthM'], 'butt joints 2 cm'),
    assumed(['waterlineDepthM'], 'the darkening spans the metre above the waterline'),
    assumed(['toneSpread'], 'per-plank luminance 4% either way, inside shipHull p10-p90'),
  ];
}

const hullCarvel: PlankingPreset = {
  ...hull,
  kind: 'planking',
  lap: 'carvel',
  seamFraction: 0.12,
  sources: hullSources(
    "a seam line 12% of a strake high, which with the wales' own shadows approaches the painted 26%"
  ),
};

const hullClinker: PlankingPreset = {
  ...hull,
  kind: 'planking',
  lap: 'clinker',
  seamFraction: 0.2,
  sources: hullSources("the step shadow on a strake's lowest 20%"),
};

const sailcloth: SailclothPreset = {
  kind: 'sailcloth',
  surfaceClass: 'sail',
  clothWidthM: [0.56, 0.76],
  seamWidthM: 0.035,
  seam: { baseTimes: 0.9 },
  bonnetAt: 0.78,
  lacingWidthM: 0.02,
  toneSpread: 0.03,
  sources: [
    { params: ['clothWidthM', 'seamWidthM', 'bonnetAt'], source: SAIL, confidence: 'medium' },
    assumed(['seam'], '"slightly darker" taken as 0.9 of the cloth'),
    assumed(['lacingWidthM'], 'the lacing drawn 2 cm wide'),
    assumed(['toneSpread'], 'per-cloth luminance 3% either way, inside sail p10-p90'),
  ],
};

const quayPiling: PilingPreset = {
  kind: 'piling',
  surfaceClass: 'shipHull',
  postSpacingM: 0.5,
  postWidthM: 0.18,
  post: { baseTimes: 1.655 },
  toneSpread: 0,
  sources: [
    { params: ['postSpacingM', 'postWidthM'], source: JUK, confidence: 'medium' },
    { params: ['post'], source: PILING_TONES, confidence: 'medium' },
    assumed(['toneSpread'], 'one post tone and one planking tone, as the painting was measured'),
  ],
};

const streetPaving: PavingPreset = {
  kind: 'paving',
  surfaceClass: 'streetGround',
  pitchM: [0.05, 0.09],
  outlinePx: [1, 2],
  outline: { near: { baseTimes: 0.74 }, nearPitchPx: 51, far: { baseTimes: 0.84 }, farPitchPx: 25 },
  toneSpread: 0.11,
  sources: [
    { params: ['pitchM'], source: PAVING_PITCH, confidence: 'low' },
    { params: ['outlinePx', 'outline'], source: PAVING_OUTLINE, confidence: 'low' },
    { params: ['toneSpread'], source: PAVING_SPREAD, confidence: 'low' },
  ],
};

export const PATTERN_PRESETS = {
  houseBrick1538,
  timberFront1538,
  roofTileHollePan,
  slateMaas,
  brickOudeKerkTower,
  brickLateGothic,
  brickLateGothicSpeklagen,
  leadSheet,
  leadedGlass,
  hullCarvel,
  hullClinker,
  sailcloth,
  quayPiling,
  streetPaving,
  brickLateGothicGobertange,
  brickLateGothicLedesteen,
  sandstoneAshlar,
} as const satisfies Readonly<Record<PresetName, PatternPreset>>;
